import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CircleCheck, Copy, FileText, IdCard, QrCode, ScanFace, ScanLine, Shield, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { GlobalContext } from '../context/GlobalContext';
import { ContainerSpinner } from '../components/Spinner';
import EnrollmentLoadingOverlay from '../components/EnrollmentLoadingOverlay';
import {
	IphoneEnrollmentInstructions,
	ManagedAndroidEnrollmentInstructions,
	NuovoEnrollmentInstructions,
	getEnrollmentError
} from '../components/EnrollmentInstructions';
import Stepper from '../components/Stepper';
import Input from '../components/Input';
import Notice from '../components/Notice';
import QrImeiScanner from '../components/QrImeiScanner';
import { Button } from '../shadcn/Button';
import { callAPI, unsafeAPI, type WarrantyDevice } from '../hooks/api';
import { format_identification_with_prefix } from '../utils/formatters';
import firestore from '../firestore';
import copy from 'copy-to-clipboard';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const SECURITY_ACTIVATION_ERROR = 'El equipo no se encuentra activo con sistema de seguridad.';
const customerSafeError = (message: string | null | undefined, fallback: string, internalFallback = SECURITY_ACTIVATION_ERROR) =>
	getEnrollmentError(message, fallback, internalFallback);

type LivenessState = {
	sessionId: string;
	documentId: string;
	qrUrl: string;
	processing?: boolean;
};

const WarrantyExchange = () => {
	const { authenticated, userData, CountrySettings, internalCountry } = useContext(GlobalContext);
	const country = internalCountry.get() || userData?.country || '';

	const [page, setPage] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const [loadingMessage, setLoadingMessage] = useState('');
	const [isEnrollmentLoading, setIsEnrollmentLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [identificationNumber, setIdentificationNumber] = useState('');
	const [liveness, setLiveness] = useState<LivenessState | null>(null);
	const [livenessVerified, setLivenessVerified] = useState(false);
	const [isCopySuccess, setIsCopySuccess] = useState(false);
	const [flow, setFlow] = useState<'warranty' | 'technical_service' | null>(null);
	const [eligibleDevices, setEligibleDevices] = useState<WarrantyDevice[]>([]);
	const [userDocumentId, setUserDocumentId] = useState('');
	const [selectedDevice, setSelectedDevice] = useState<WarrantyDevice | null>(null);
	const [targetImei, setTargetImei] = useState('');
	const [deviceManagementProvider, setDeviceManagementProvider] = useState('');
	const [showQrScanner, setShowQrScanner] = useState(false);
	const [contractGenerated, setContractGenerated] = useState(false);
	const [warrantyAnexoId, setWarrantyAnexoId] = useState('');
	const [anexoAccepted, setAnexoAccepted] = useState(false);
	const [warrantyReleasePending, setWarrantyReleasePending] = useState(false);
	const [bypassCode, setBypassCode] = useState('');
	const [bypassCodeLoading, setBypassCodeLoading] = useState(false);
	const [bypassCodeError, setBypassCodeError] = useState('');

	const formattedIdentification = useMemo(
		() => format_identification_with_prefix(identificationNumber, CountrySettings?.uid_prefix || ''),
		[CountrySettings?.uid_prefix, identificationNumber]
	);

	const loadDevices = useCallback(
		async (nextFlow: 'warranty' | 'technical_service') => {
			if (!userData || !formattedIdentification || !liveness?.documentId) {
				setErrorMessage('La verificación biométrica expiró. Inicie el proceso nuevamente.');
				return;
			}

			setIsLoading(true);
			setLoadingMessage(nextFlow === 'warranty' ? 'Buscando equipos para garantía' : 'Buscando equipos liberados temporalmente');
			setErrorMessage('');
			setEligibleDevices([]);
			setSelectedDevice(null);
			try {
				const [data, error] = await callAPI(
					'https://api.krece.app/registration/cambiodegarantia/',
					'POST',
					{
						identification_number: formattedIdentification,
						garantia_action: nextFlow === 'warranty' ? 'list_warranty_devices' : 'list_technical_service_devices',
						liveness_document_id: liveness.documentId
					},
					userData,
					country
				);

				if (error) {
					setErrorMessage(customerSafeError(error, 'No pudimos consultar los equipos. Intente nuevamente.'));
					return;
				}

				const devices = data?.devices || [];
				setFlow(nextFlow);
				setEligibleDevices(devices);
				setUserDocumentId(data?.user_document_id || '');
				setPage(nextFlow === 'warranty' ? 11 : 12);
				if (!devices.length) {
					setErrorMessage(
						nextFlow === 'warranty'
							? 'No hay equipos que cumplan las condiciones para cambio por garantía.'
							: 'No hay ningún equipo liberado temporalmente. Por favor, contacta al servicio técnico.'
					);
				}
			} catch (error) {
				setErrorMessage('Estamos teniendo problemas para conectarnos.');
			} finally {
				setIsLoading(false);
				setLoadingMessage('');
			}
		},
		[country, formattedIdentification, liveness?.documentId, userData]
	);

	useEffect(() => {
		if (!liveness?.documentId || livenessVerified) return;

		const unsubscribe = firestore
			.collection('LivenessSessions')
			.doc(liveness.documentId)
			.onSnapshot(
				(snapshot) => {
					if (!snapshot.exists) {
						setErrorMessage('Esta sesión de verificación biométrica expiró. Genere una nueva sesión.');
						setLiveness(null);
						return;
					}

					const data = snapshot.data() || {};
					const isSuccess = data.valid_user === true && data.user_authenticated === true && data.photo_id_url;
					if (isSuccess) {
						setLivenessVerified(true);
						setErrorMessage('');
						setPage(3);
						return;
					}

					if (data.valid_user === false || data.user_authenticated === false) {
						setErrorMessage('La verificación biométrica no coincidió con el cliente. Intente nuevamente.');
					}
				},
				() => {
					setErrorMessage('Ocurrió un error iniciando la verificación biométrica, por favor intente más tarde.');
				}
			);

		return () => unsubscribe();
	}, [liveness?.documentId, livenessVerified]);

	useEffect(() => {
		if (flow !== 'warranty' || selectedDevice?.warranty_mode !== 'active' || !warrantyAnexoId) {
			setAnexoAccepted(false);
			return;
		}

		const unsubscribe = firestore
			.collection('Anexos')
			.doc(warrantyAnexoId)
			.onSnapshot((snapshot) => {
				if (!snapshot.exists) {
					setAnexoAccepted(false);
					return;
				}

				const data = snapshot.data() || {};
				const matchesExchange =
					data.related_device?.id === selectedDevice.device_document_id &&
					data.warranty_exchange_target_imei === targetImei;
				setAnexoAccepted(matchesExchange && data.terms_accepted === true);
			});

		return () => unsubscribe();
	}, [flow, selectedDevice, targetImei, warrantyAnexoId]);

	const startLiveness = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrorMessage('');

		if (!identificationNumber.trim()) {
			setErrorMessage('Ingrese la identificación del cliente.');
			return;
		}

		setIsLoading(true);
		setLoadingMessage('Generando verificación biométrica');
		setLivenessVerified(false);
		setLiveness(null);
		setIsCopySuccess(false);
		setFlow(null);
		setEligibleDevices([]);
		setSelectedDevice(null);
		setContractGenerated(false);
		setWarrantyAnexoId('');
		setAnexoAccepted(false);
		setTargetImei('');
		setDeviceManagementProvider('');

		try {
			const [data, error] = await callAPI(
				'https://api.krece.app/authentication/liveness-detection-session/',
				'GET',
				{ user_identification_number: formattedIdentification },
				userData,
				country,
				false
			);

			if (error || !data?.session_id || !data?.document_id) {
				setErrorMessage(customerSafeError(error, 'Ocurrió un error iniciando la verificación biométrica.'));
				return;
			}

			setLiveness({
				sessionId: data.session_id,
				documentId: data.document_id,
				qrUrl: `${window.location.origin}/liveness-detection?liveness_session_id=${data.session_id}&document_id=${data.document_id}&user_identification_number=${encodeURIComponent(formattedIdentification)}`
			});
			setPage(2);
		} catch (error) {
			setErrorMessage('Estamos teniendo problemas para conectarnos.');
		} finally {
			setIsLoading(false);
			setLoadingMessage('');
		}
	};

	const cancelLiveness = () => {
		setLiveness(null);
		setLivenessVerified(false);
		setIsCopySuccess(false);
		setPage(1);
	};

	const selectWarrantyDevice = (device: WarrantyDevice) => {
		setFlow('warranty');
		setSelectedDevice(device);
		const releasePending = device.warranty_old_imei_release_pending === true;
		setTargetImei(releasePending ? device.warranty_last_target_imei || device.device_imei_no || '' : '');
		setDeviceManagementProvider(releasePending ? device.device_management_provider || '' : '');
		setContractGenerated(false);
		setWarrantyAnexoId('');
		setAnexoAccepted(false);
		setShowQrScanner(false);
		setWarrantyReleasePending(releasePending);
		setBypassCode('');
		setBypassCodeError('');
		setErrorMessage('');
		setPage(device.warranty_mode === 'fully_paid' ? 9 : releasePending ? 6 : 4);
	};

	const selectTechnicalDevice = (device: WarrantyDevice) => {
		setFlow('technical_service');
		setSelectedDevice(device);
		setTargetImei(device.device_imei_no || '');
		setDeviceManagementProvider('');
		setBypassCode('');
		setBypassCodeError('');
		setErrorMessage('');
		setPage(13);
	};

	const handleGetBypassCode = async () => {
		if (!targetImei) return;
		setBypassCodeLoading(true);
		setBypassCode('');
		setBypassCodeError('');
		try {
			const result = await unsafeAPI(
				'POST',
				'https://api.krece.app/administration/kandjibypasscode/',
				{ device_imei_no: targetImei },
				userData,
				country
			);
			if (result?.success && result.data) {
				setBypassCode(result.data);
			} else {
				setBypassCodeError(
					customerSafeError(result?.status, 'No se pudo obtener el código de seguridad.', 'No se pudo obtener el código de seguridad.')
				);
			}
		} catch {
			setBypassCodeError('Error al obtener el código de bypass');
		} finally {
			setBypassCodeLoading(false);
		}
	};

	const completePaidDeviceExchange = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrorMessage('');
		if (!selectedDevice || selectedDevice.warranty_mode !== 'fully_paid' || !liveness?.documentId) {
			setErrorMessage('Faltan datos para completar el cambio.');
			return;
		}

		const cleanedImei = targetImei.trim();
		if (cleanedImei.length !== 15) {
			setErrorMessage('IMEI inválido, asegúrese de que el IMEI tenga 15 dígitos.');
			return;
		}

		setIsLoading(true);
		setLoadingMessage('Registrando IMEI nuevo');
		try {
			const [, error] = await callAPI(
				'https://api.krece.app/registration/cambiodegarantia/',
				'POST',
				{
					identification_number: formattedIdentification,
					garantia_action: 'complete_paid_device_exchange',
					device_document_id: selectedDevice.device_document_id,
					original_imei: selectedDevice.device_imei_no || '',
					target_imei: cleanedImei,
					liveness_document_id: liveness.documentId
				},
				userData,
				country
			);

			if (error) {
				setErrorMessage(customerSafeError(error, 'No se pudo completar el cambio. Intente nuevamente.'));
				return;
			}

			setTargetImei(cleanedImei);
			setShowQrScanner(false);
			setPage(10);
		} catch (error) {
			setErrorMessage('Estamos teniendo problemas para conectarnos.');
		} finally {
			setIsLoading(false);
			setLoadingMessage('');
		}
	};

	const activateImei = async (device: WarrantyDevice, imei: string, technicalService = false) => {
		const startTime = Date.now();
		const maxDuration = 3 * 60 * 1000;
		while (Date.now() - startTime < maxDuration) {
			const controller = new AbortController();
			const timeoutId = window.setTimeout(() => controller.abort(), 45000);
			const response = await unsafeAPI(
				'POST',
				'https://api.krece.app/registration/registerimeiv2/',
				{
					device_imei: imei,
					device_type: device.device_type,
					country: device.country || country,
					...(device.device_manufacturer && { device_manufacturer: device.device_manufacturer }),
					...(technicalService
						? {
								technical_service_device_document_id: device.device_document_id,
								technical_service_identification_number: formattedIdentification,
								technical_service_liveness_document_id: liveness?.documentId
							}
						: {
								warranty_exchange_device_document_id: device.device_document_id,
								warranty_exchange_identification_number: formattedIdentification,
								warranty_exchange_liveness_document_id: liveness?.documentId
							})
				},
				userData,
				country,
				true,
				false,
				controller.signal
			);
			window.clearTimeout(timeoutId);

			if (response?.success === false || response?.activating_status === 'failed') {
				setErrorMessage(customerSafeError(response?.status, SECURITY_ACTIVATION_ERROR));
				return null;
			}
			if (response?.activating_status === 'activated' && response.device_management_provider) {
				return response.device_management_provider as string;
			}
			// Network errors, request timeouts and confirming responses remain retryable.
			await sleep(10000);
		}
		setErrorMessage('Se alcanzó el tiempo máximo de espera. Intente nuevamente o contacte a soporte técnico.');
		return null;
	};

	const registerWarrantyImei = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrorMessage('');
		if (!selectedDevice) return;

		const cleanedImei = targetImei.trim();
		if (cleanedImei.length !== 15) {
			setErrorMessage('IMEI inválido, asegúrese de que el IMEI tenga 15 dígitos.');
			return;
		}
		setTargetImei(cleanedImei);
		setIsLoading(true);
		setIsEnrollmentLoading(true);
		setLoadingMessage('Registrando equipo');
		try {
			const provider = await activateImei(selectedDevice, cleanedImei);
			if (!provider) return;
			setDeviceManagementProvider(provider);
			setPage(provider === 'nuovo' ? 5 : 8);
		} catch (error) {
			setErrorMessage('Estamos teniendo problemas para conectarnos.');
		} finally {
			setIsLoading(false);
			setIsEnrollmentLoading(false);
			setLoadingMessage('');
		}
	};

	const registerTechnicalServiceImei = async () => {
		setErrorMessage('');
		if (!selectedDevice?.device_imei_no || !liveness?.documentId) {
			setErrorMessage('No pudimos validar el equipo o la biometría seleccionada.');
			return;
		}

		setIsLoading(true);
		setIsEnrollmentLoading(true);
		setLoadingMessage('Registrando equipo');
		try {
			const provider = await activateImei(selectedDevice, selectedDevice.device_imei_no, true);
			if (!provider) return;
			setDeviceManagementProvider(provider);
			setTargetImei(selectedDevice.device_imei_no);
			setPage(provider === 'nuovo' ? 14 : 15);
		} catch (error) {
			setErrorMessage('Estamos teniendo problemas para conectarnos.');
		} finally {
			setIsLoading(false);
			setIsEnrollmentLoading(false);
			setLoadingMessage('');
		}
	};

	const completeTechnicalServiceReenrollment = async () => {
		setErrorMessage('');
		if (!selectedDevice?.device_imei_no || !liveness?.documentId) {
			setErrorMessage('Faltan datos para completar el reenrolamiento.');
			return;
		}

		setIsLoading(true);
		setLoadingMessage('Confirmando reenrolamiento');
		try {
			const [, error] = await callAPI(
				'https://api.krece.app/registration/cambiodegarantia/',
				'POST',
				{
					identification_number: formattedIdentification,
					garantia_action: 'complete_technical_service_reenrollment',
					device_document_id: selectedDevice.device_document_id,
					target_imei: selectedDevice.device_imei_no,
					liveness_document_id: liveness.documentId
				},
				userData,
				country
			);
			if (error) {
				setErrorMessage(customerSafeError(error, SECURITY_ACTIVATION_ERROR));
				return;
			}
			setPage(16);
		} catch (error) {
			setErrorMessage('Estamos teniendo problemas para conectarnos.');
		} finally {
			setIsLoading(false);
			setLoadingMessage('');
		}
	};

	const generateAnexo = async () => {
		setErrorMessage('');
		if (!selectedDevice || !targetImei || !userDocumentId || !liveness?.documentId) {
			setErrorMessage('Faltan datos para generar el contrato.');
			return;
		}

		setIsLoading(true);
		setLoadingMessage('Generando contrato');
		try {
			const [data, error] = await callAPI(
				'https://api.krece.app/registration/cambiodegarantia/',
				'POST',
				{
					identification_number: formattedIdentification,
					garantia_action: 'generate_warranty_contract',
					device_document_id: selectedDevice.device_document_id,
					target_imei: targetImei,
					liveness_document_id: liveness.documentId
				},
				userData,
				country
			);

			if (error) {
				setErrorMessage(customerSafeError(error, 'No se pudo generar el contrato. Intente nuevamente.'));
				return;
			}
			if (!data?.anexo_document_id) {
				setErrorMessage('No se pudo identificar el contrato generado. Intente nuevamente.');
				return;
			}

			setWarrantyAnexoId(data.anexo_document_id);
			setAnexoAccepted(data.terms_accepted === true);
			setContractGenerated(true);
		} catch (error) {
			setErrorMessage('Estamos teniendo problemas para conectarnos.');
		} finally {
			setIsLoading(false);
			setLoadingMessage('');
		}
	};

	const finalizeWarranty = async () => {
		setErrorMessage('');
		if (
			!selectedDevice ||
			!targetImei ||
			!liveness?.documentId ||
			(!warrantyReleasePending && !warrantyAnexoId)
		) {
			setErrorMessage('Faltan datos para concretar el cambio.');
			return;
		}

		setIsLoading(true);
		setLoadingMessage('Concretando cambio');
		try {
			const [, error] = await callAPI(
				'https://api.krece.app/registration/cambiodegarantia/',
				'POST',
				{
					identification_number: formattedIdentification,
					garantia_action: 'change',
					device_document_id: selectedDevice.device_document_id,
					target_imei: targetImei,
					liveness_document_id: liveness.documentId,
					...(warrantyAnexoId && { anexo_document_id: warrantyAnexoId })
				},
				userData,
				country
			);

			if (error) {
				if (error.includes('no pudimos liberar el IMEI anterior')) {
					setWarrantyReleasePending(true);
				}
				setErrorMessage(customerSafeError(error, 'No se pudo completar el cambio. Intente nuevamente.'));
				return;
			}

			setWarrantyReleasePending(false);
			setPage(7);
		} catch (error) {
			setErrorMessage('Estamos teniendo problemas para conectarnos.');
		} finally {
			setIsLoading(false);
			setLoadingMessage('');
		}
	};

	const resetFlow = () => {
		setPage(1);
		setIsEnrollmentLoading(false);
		setErrorMessage('');
		setIdentificationNumber('');
		setLiveness(null);
		setLivenessVerified(false);
		setFlow(null);
		setEligibleDevices([]);
		setUserDocumentId('');
		setSelectedDevice(null);
		setTargetImei('');
		setDeviceManagementProvider('');
		setContractGenerated(false);
		setWarrantyAnexoId('');
		setAnexoAccepted(false);
		setWarrantyReleasePending(false);
		setBypassCode('');
		setBypassCodeError('');
	};

	const commonSteps = [
		{ label: country === 'peru' ? 'DNI' : 'Cédula', pages: [1] },
		{ label: 'Biometría', pages: [2] },
		{ label: 'Proceso', pages: [3] }
	];
	const steps =
		flow === 'technical_service'
			? [
					...commonSteps,
					{ label: 'Equipo', pages: [12] },
					{ label: 'Reenrolamiento', pages: [13, 14, 15] },
					{ label: 'Finalizar', pages: [16] }
				]
			: selectedDevice?.warranty_mode === 'fully_paid'
				? [...commonSteps, { label: 'Equipo', pages: [11] }, { label: 'IMEI nuevo', pages: [9] }, { label: 'Finalizar', pages: [10] }]
				: [
						...commonSteps,
						{ label: 'Equipo', pages: [11] },
						{ label: 'IMEI', pages: [4] },
						{ label: 'Configuración', pages: [5, 8] },
						{ label: 'Contrato', pages: [6] },
						{ label: 'Finalizar', pages: [7] }
					];

	const renderManagedEnrollmentInstructions = () =>
		selectedDevice?.device_type === 'iphone' ? (
			<IphoneEnrollmentInstructions
				imei={targetImei}
				bypassCode={bypassCode}
				bypassCodeLoading={bypassCodeLoading}
				bypassCodeError={bypassCodeError}
				onGetBypassCode={handleGetBypassCode}
				onCopyBypassCode={() => copy(bypassCode)}
			/>
		) : (
			<ManagedAndroidEnrollmentInstructions />
		);

	if (!authenticated || !userData) return <ContainerSpinner size={3} />;

	return (
		<div className="m-auto flex min-h-full min-w-full flex-col items-center justify-start">
			<Stepper currentPage={page} steps={steps} />
			<EnrollmentLoadingOverlay
				title={isEnrollmentLoading ? loadingMessage : undefined}
				description={isEnrollmentLoading ? 'Esto puede tardar unos segundos, por favor NO encienda el teléfono aún.' : undefined}
			/>
			<div className="m-auto flex h-full w-full flex-col items-center justify-center overflow-x-hidden p-4 md:p-6">
				<div className="w-[560px] max-w-full rounded-xl border-[1.5px] border-neutral-900 bg-neutral-1000/25 p-6 shadow-lg duration-300 animate-in fade-in slide-in-from-bottom-5 light:bg-white light:shadow-none md:bg-background/50 md:p-8">
					{page !== 3 && !isEnrollmentLoading && isLoading && loadingMessage && (
						<div className="mb-4">
							<Notice message={loadingMessage} variant="info" />
						</div>
					)}

					{page === 1 && (
						<>
							{render_page_header(
								'Garantía y servicio técnico',
								`Ingrese ${country === 'peru' ? 'el DNI' : 'la cédula'} del cliente para iniciar el cambio`,
								<IdCard />
							)}

							<form className="space-y-4" onSubmit={startLiveness}>
								<Input
									name="identification_number"
									floatingLabel={country === 'peru' ? 'DNI del cliente' : 'Cédula del cliente'}
									value={identificationNumber}
									onChange={(value) => setIdentificationNumber(value.trim())}
									placeholder="Ingrese la identificación"
									allowedChars="numbers_only"
								/>

								{errorMessage && <Notice message={errorMessage} variant="error" />}

								<Button disabled={isLoading || !identificationNumber} loading={isLoading} type="submit" variant="primary">
									Continuar a biometría
								</Button>
							</form>
						</>
					)}

					{page === 2 && (
						<>
							{render_page_header(
								'Verificación biométrica',
								'El cliente debe completar el liveness check para continuar.',
								<ScanFace />
							)}

							<div className="space-y-5">
								{liveness?.qrUrl && (
									<div className="flex flex-col items-center gap-4 rounded-xl border border-neutral-800 bg-neutral-950/50 p-5">
										<div className="rounded-xl bg-white p-3">
											<QRCodeSVG value={liveness.qrUrl} size={210} bgColor="#ffffff" fgColor="#000000" level="M" />
										</div>
										<button
											type="button"
											onClick={() => {
												copy(liveness.qrUrl);
												setIsCopySuccess(true);
												setTimeout(() => setIsCopySuccess(false), 2000);
											}}
											className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${
												isCopySuccess
													? 'border-green-500/50 bg-green-500/20 text-green-400 hover:border-green-500/70 hover:bg-green-500/30'
													: 'border-neutral-600 bg-neutral-800/50 text-neutral-300 hover:border-neutral-500 hover:bg-neutral-700/50 hover:text-neutral-200'
											}`}
											title="Copiar enlace del QR"
										>
											{isCopySuccess ? (
												<>
													<CircleCheck className="size-4" />
													<span>¡Copiado!</span>
												</>
											) : (
												<>
													<Copy className="size-4" />
													<span>Copiar enlace</span>
												</>
											)}
										</button>
										<p className="text-center text-sm text-neutral-300">
											Escanee este QR con el teléfono del cliente. Avanzaremos automáticamente cuando la verificación sea exitosa.
										</p>
										<a
											className="text-sm text-primary-400 underline hover:text-primary-300"
											href={liveness.qrUrl}
											target="_blank"
											rel="noreferrer"
										>
											Abrir enlace de verificación
										</a>
									</div>
								)}

								{livenessVerified ? (
									<Notice message="Biometría verificada. Seleccione el proceso que desea realizar." variant="success" />
								) : (
									<Notice message="Esperando que el cliente complete la verificación biométrica." variant="info" />
								)}

								{errorMessage && <Notice message={errorMessage} variant="error" />}

								<Button
									disabled={isLoading}
									type="button"
									onClick={cancelLiveness}
									variant="secondary"
									Icon={<ArrowLeft className="size-5" />}
								>
									Atrás
								</Button>
							</div>
						</>
					)}

					{page === 3 &&
						(isLoading ? (
							<div className="flex min-h-64 flex-col items-center justify-center gap-4">
								<ContainerSpinner size={3} className="h-auto" />
								<p className="text-center text-sm text-neutral-300">{loadingMessage}</p>
							</div>
						) : (
							<>
								{render_page_header(
									'¿Qué desea realizar?',
									'Seleccione el proceso que corresponde al equipo del cliente.',
									<Smartphone />
								)}

								<div className="space-y-4">
									<Button type="button" onClick={() => void loadDevices('warranty')} variant="primary">
										Cambio por garantía
									</Button>
									<Button type="button" onClick={() => void loadDevices('technical_service')} variant="secondary">
										Reenrolamiento de servicio técnico
									</Button>
									{errorMessage && <Notice message={errorMessage} variant="error" />}
									<Button
										type="button"
										onClick={() => setPage(2)}
										variant="secondary"
										Icon={<ArrowLeft className="size-5" />}
									>
										Atrás
									</Button>
								</div>
							</>
						))}

					{page === 4 && selectedDevice && (
						<>
							{render_page_header('Nuevo IMEI', 'Ingrese el IMEI del equipo que reemplazará al actual.', <QrCode />)}

							<form className="space-y-4" onSubmit={registerWarrantyImei}>
								<div className="rounded-lg border border-neutral-800 p-4 text-sm text-neutral-300">
									<p>
										Equipo actual:{' '}
										{selectedDevice.device_commercial_name || selectedDevice.device_model || selectedDevice.device_type}
									</p>
									<p>El precio, las cuotas y el calendario de pagos se mantienen sin cambios.</p>
								</div>

								<Input
									floatingLabel="IMEI del equipo nuevo"
									value={targetImei}
									onChange={(value) => {
										setTargetImei(value.trim());
										setWarrantyAnexoId('');
										setContractGenerated(false);
										setAnexoAccepted(false);
									}}
									placeholder="Ingrese el IMEI nuevo"
									allowedChars="numbers_only"
									maxLength={15}
								/>

								<button
									type="button"
									onClick={() => setShowQrScanner(true)}
									className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-700 bg-neutral-950/50 px-4 py-2.5 text-sm text-neutral-300 transition-colors hover:border-neutral-500 hover:bg-neutral-900/50 hover:text-neutral-100"
								>
									<ScanLine className="size-4" />
									Escanear código QR de la caja
								</button>

								{showQrScanner && (
									<QrImeiScanner
										onScan={(imei) => {
											setTargetImei(imei);
											setWarrantyAnexoId('');
											setContractGenerated(false);
											setAnexoAccepted(false);
											setShowQrScanner(false);
											setErrorMessage('');
										}}
										onClose={() => setShowQrScanner(false)}
									/>
								)}

								{errorMessage && <Notice message={errorMessage} variant="error" />}

								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button disabled={isLoading || targetImei.length !== 15} type="submit" variant="primary">
										Activar equipo
									</Button>
									<Button
										disabled={isLoading}
										type="button"
										onClick={() => setPage(11)}
										variant="secondary"
										Icon={<ArrowLeft className="size-5" />}
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}

					{page === 5 && (
						<>
							{render_page_header('Configuración de seguridad', null, <Shield />)}

							<div className="space-y-6">
								<NuovoEnrollmentInstructions country={country} />
								{errorMessage && <Notice message={errorMessage} variant="error" />}
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button type="button" onClick={() => setPage(6)} variant="primary">
										Continuar al contrato
									</Button>
									<Button type="button" onClick={() => setPage(4)} variant="secondary" Icon={<ArrowLeft className="size-5" />}>
										Atrás
									</Button>
								</div>
							</div>
						</>
					)}

					{page === 6 && selectedDevice && (
						<>
							{render_page_header(
								'Contrato nuevo',
								'El cliente debe aceptar un contrato nuevo antes de concretar el cambio.',
								<FileText />
							)}

							<div className="space-y-4">
								<div className="rounded-lg border border-neutral-800 p-4 text-sm text-neutral-300">
									<p>IMEI actual: {selectedDevice.device_imei_no || '—'}</p>
									<p>IMEI nuevo: {targetImei}</p>
									<p>Precio y cuotas: sin cambios</p>
								</div>

								{anexoAccepted ? (
									<Notice message="Contrato aceptado" variant="success" />
								) : contractGenerated ? (
									<Notice message="Contrato generado. Esperando aceptación del cliente en la app Krece." variant="info" />
								) : (
									<Notice message="Genere el contrato para que el cliente lo acepte en la app Krece." variant="info" />
								)}

								{!anexoAccepted && !warrantyReleasePending && (
									<Button disabled={isLoading} loading={isLoading} type="button" onClick={generateAnexo} variant="secondary">
										{contractGenerated ? 'Generar otro contrato' : 'Generar contrato'}
									</Button>
								)}

								{warrantyReleasePending && (
									<Notice message="El cambio ya fue aplicado. Reintente para liberar únicamente el IMEI anterior." variant="warning" />
								)}
								{errorMessage && <Notice message={errorMessage} variant="error" />}

								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										disabled={isLoading || (!anexoAccepted && !warrantyReleasePending)}
										loading={isLoading}
										type="button"
										onClick={finalizeWarranty}
										variant="primary"
									>
										Completar cambio por garantía
									</Button>
									<Button
										disabled={isLoading}
										type="button"
										onClick={() => setPage(deviceManagementProvider === 'nuovo' ? 5 : 8)}
										variant="secondary"
										Icon={<ArrowLeft className="size-5" />}
									>
										Atrás
									</Button>
								</div>
							</div>
						</>
					)}

					{page === 7 && (
						<>
							{render_page_header(
								'¡Cambio por garantía exitoso!',
								'El equipo del cliente fue actualizado sin cambiar precio ni cuotas.',
								<CircleCheck />
							)}

							<div className="space-y-4">
								<Notice
									message="El ID del documento del equipo permanece igual. Solo se actualizaron los datos del equipo nuevo."
									variant="success"
								/>
								{selectedDevice?.device_type === 'iphone' && (
									<Notice
										message="Para la liberación definitiva del equipo, por favor escribe a Atención al Aliado (ATA) vía el grupo de WhatsApp."
										variant="warning"
									/>
								)}
								<Button type="button" onClick={resetFlow} variant="primary">
									Realizar otro cambio
								</Button>
							</div>
						</>
					)}

					{page === 8 && (
						<>
							{render_page_header('Configuración de seguridad', null, <Shield />)}
							<div className="space-y-4">
								{renderManagedEnrollmentInstructions()}
								{errorMessage && <Notice message={errorMessage} variant="error" />}
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button type="button" onClick={() => setPage(6)} variant="primary">
										Continuar al contrato
									</Button>
									<Button type="button" onClick={() => setPage(4)} variant="secondary" Icon={<ArrowLeft className="size-5" />}>
										Atrás
									</Button>
								</div>
							</div>
						</>
					)}

					{page === 11 && (
						<>
							{render_page_header(
								'Equipo a reemplazar',
								'Seleccione un equipo activo con cuotas pendientes o uno completamente pagado comprado en los últimos dos meses.',
								<Smartphone />
							)}

							<div className="space-y-4">
								{errorMessage && <Notice message={errorMessage} variant="error" />}
								{eligibleDevices.map((device) => (
									<button
										key={device.device_document_id}
										type="button"
										onClick={() => selectWarrantyDevice(device)}
										className="w-full rounded-xl border border-neutral-800 bg-neutral-950/40 p-4 text-left transition-colors hover:border-primary-500/60 hover:bg-primary-500/10"
									>
										<h3 className="font-semibold text-neutral-0">
											{device.device_commercial_name || device.device_model || device.device_type}
										</h3>
										<p className="text-sm text-neutral-300">IMEI: {device.device_imei_no || '—'}</p>
										<p className="text-sm text-neutral-300">
											{device.warranty_mode === 'active'
												? 'Activo con cuotas pendientes'
												: 'Completamente pagado · compra menor a dos meses'}
										</p>
									</button>
								))}
								<Button
									disabled={isLoading}
									type="button"
									onClick={() => {
										setFlow(null);
										setErrorMessage('');
										setPage(3);
									}}
									variant="secondary"
									Icon={<ArrowLeft className="size-5" />}
								>
									Atrás
								</Button>
							</div>
						</>
					)}

					{page === 12 && (
						<>
							{render_page_header(
								'Equipo de servicio técnico',
								'Seleccione el equipo que fue liberado temporalmente para servicio técnico.',
								<Shield />
							)}

							<div className="space-y-4">
								{errorMessage && <Notice message={errorMessage} variant="info" />}
								{eligibleDevices.map((device) => (
									<button
										key={device.device_document_id}
										type="button"
										onClick={() => selectTechnicalDevice(device)}
										className="w-full rounded-xl border border-neutral-800 bg-neutral-950/40 p-4 text-left transition-colors hover:border-primary-500/60 hover:bg-primary-500/10"
									>
										<h3 className="font-semibold text-neutral-0">
											{device.device_commercial_name || device.device_model || device.device_type}
										</h3>
										<p className="text-sm text-neutral-300">IMEI: {device.device_imei_no || '—'}</p>
										<p className="text-sm text-neutral-300">Liberado temporalmente</p>
									</button>
								))}
								<Button
									disabled={isLoading}
									type="button"
									onClick={() => {
										setFlow(null);
										setErrorMessage('');
										setPage(3);
									}}
									variant="secondary"
									Icon={<ArrowLeft className="size-5" />}
								>
									Atrás
								</Button>
							</div>
						</>
					)}

					{page === 9 && selectedDevice?.warranty_mode === 'fully_paid' && (
						<>
							{render_page_header(
								'IMEI del equipo nuevo',
								'Este IMEI quedará registrado como utilizado y no podrá financiarse posteriormente en Krece.',
								<QrCode />
							)}

							<form className="space-y-4" onSubmit={completePaidDeviceExchange}>
								<div className="rounded-lg border border-neutral-800 p-4 text-sm text-neutral-300">
									<p>Equipo pagado: {selectedDevice.device_commercial_name || selectedDevice.device_model || 'Equipo'}</p>
									<p>IMEI anterior: {selectedDevice.device_imei_no}</p>
								</div>

								<Input
									floatingLabel="IMEI del equipo nuevo"
									value={targetImei}
									onChange={(value) => setTargetImei(value.trim())}
									placeholder="Ingrese el IMEI nuevo"
									allowedChars="numbers_only"
									maxLength={15}
								/>

								<button
									type="button"
									onClick={() => setShowQrScanner(true)}
									className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-700 bg-neutral-950/50 px-4 py-2.5 text-sm text-neutral-300 transition-colors hover:border-neutral-500 hover:bg-neutral-900/50 hover:text-neutral-100"
								>
									<ScanLine className="size-4" />
									Escanear IMEI del equipo nuevo
								</button>

								{showQrScanner && (
									<QrImeiScanner
										onScan={(imei) => {
											setTargetImei(imei);
											setShowQrScanner(false);
											setErrorMessage('');
										}}
										onClose={() => setShowQrScanner(false)}
									/>
								)}

								{errorMessage && <Notice message={errorMessage} variant="error" />}

								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button disabled={isLoading || targetImei.length !== 15} loading={isLoading} type="submit" variant="primary">
										Confirmar cambio
									</Button>
									<Button
										disabled={isLoading}
										type="button"
										onClick={() => {
											setTargetImei('');
											setErrorMessage('');
											setPage(11);
										}}
										variant="secondary"
										Icon={<ArrowLeft className="size-5" />}
									>
										Atrás
									</Button>
								</div>
							</form>
						</>
					)}

					{page === 10 && (
						<>
							{render_page_header('Cambio por garantía completado', 'El IMEI nuevo quedó registrado como utilizado.', <CircleCheck />)}

							<div className="space-y-4">
								<Notice
									message="No hay necesidad de configurar el nuevo equipo porque ya está completamente pago."
									variant="success"
								/>
								<Button type="button" onClick={resetFlow} variant="primary">
									Realizar otro cambio
								</Button>
							</div>
						</>
					)}

					{page === 13 && selectedDevice && (
						<>
							{render_page_header(
								'Reenrolamiento de servicio técnico',
								'El equipo será reenrolado usando el mismo proceso de seguridad de una venta nueva.',
								<Shield />
							)}
							<div className="space-y-4">
								<div className="rounded-lg border border-neutral-800 p-4 text-sm text-neutral-300">
									<p>Equipo: {selectedDevice.device_commercial_name || selectedDevice.device_model || 'Equipo'}</p>
									<p>IMEI: {selectedDevice.device_imei_no}</p>
								</div>
								{errorMessage && <Notice message={errorMessage} variant="error" />}
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button disabled={isLoading} type="button" onClick={registerTechnicalServiceImei} variant="primary">
										Reenrolar equipo
									</Button>
									<Button
										disabled={isLoading}
										type="button"
										onClick={() => setPage(12)}
										variant="secondary"
										Icon={<ArrowLeft className="size-5" />}
									>
										Atrás
									</Button>
								</div>
							</div>
						</>
					)}

					{page === 14 && selectedDevice && (
						<>
							{render_page_header('Configuración de seguridad', null, <Shield />)}
							<div className="space-y-6">
								<NuovoEnrollmentInstructions country={country} />
								{errorMessage && <Notice message={errorMessage} variant="error" />}
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										disabled={isLoading}
										loading={isLoading}
										type="button"
										onClick={completeTechnicalServiceReenrollment}
										variant="primary"
									>
										Finalizar reenrolamiento
									</Button>
									<Button
										disabled={isLoading}
										type="button"
										onClick={() => setPage(13)}
										variant="secondary"
										Icon={<ArrowLeft className="size-5" />}
									>
										Atrás
									</Button>
								</div>
							</div>
						</>
					)}

					{page === 15 && selectedDevice && (
						<>
							{render_page_header('Configuración de seguridad', null, <Shield />)}
							<div className="space-y-4">
								{renderManagedEnrollmentInstructions()}
								{errorMessage && <Notice message={errorMessage} variant="error" />}
								<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
									<Button
										disabled={isLoading}
										loading={isLoading}
										type="button"
										onClick={completeTechnicalServiceReenrollment}
										variant="primary"
									>
										Finalizar reenrolamiento
									</Button>
									<Button
										disabled={isLoading}
										type="button"
										onClick={() => setPage(13)}
										variant="secondary"
										Icon={<ArrowLeft className="size-5" />}
									>
										Atrás
									</Button>
								</div>
							</div>
						</>
					)}

					{page === 16 && (
						<>
							{render_page_header(
								'Reenrolamiento completado',
								'El equipo está nuevamente activo y listo para continuar.',
								<CircleCheck />
							)}
							<div className="space-y-4">
								<Notice message="El equipo fue reenrolado sin cambiar cuotas ni precios." variant="success" />
								<Button type="button" onClick={resetFlow} variant="primary">
									Realizar otro proceso
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);

	function render_page_header(title: string, description: string | null, icon: React.ReactElement) {
		return (
			<div className="mb-4 flex flex-col items-center gap-2 md:mb-6 md:gap-3">
				<div className="relative">
					<div className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
					<div className="relative rounded-full bg-neutral-900 p-4 light:bg-neutral-1000 light:ring-2 light:ring-primary-500/10">
						{icon &&
							React.cloneElement(icon, {
								className: 'h-6 w-6 text-primary-500'
							})}
					</div>
				</div>
				<div className="space-y-1 text-center">
					<h1 className="text-2xl font-semibold tracking-tighter text-neutral-0 md:text-3xl">{title}</h1>
					{description && <p className="text-neutral-300">{description}</p>}
				</div>
			</div>
		);
	}
};

export default WarrantyExchange;
